<template>
  <div class="iq-card compass-card h-100 w-100">
    <div class="iq-card-body">
      <div class="fms-card-header">
        <div>
          <h4>Navigation &amp; Sea Conditions</h4>
          <p class="text-muted">Heading, speed, and wave impact overview.</p>
        </div>
      </div>
      <div class="compass-viewport">
        <div class="compass-scale" :style="compassRotationStyle">
          <div
            v-for="deg in compassMarks"
            :key="`mark-${deg}`"
            class="degree-mark"
            :style="{ transform: `rotate(${deg}deg)` }"
          >
            <div v-if="deg % 30 === 0" class="degree-label">{{ deg }}</div>
          </div>
          <div
            v-for="point in compassCardinals"
            :key="`card-${point.deg}`"
            class="cardinal-point"
            :style="{ transform: `rotate(${point.deg}deg)` }"
          >
            <div class="cardinal-label">{{ point.label }}</div>
          </div>
        </div>
        <div
          class="wave-indicator"
          :class="{ 'wave-indicator--hidden': waveDirection === null }"
          :style="waveRotationStyle"
        >
          <div class="wave-arrow">
            <svg
              width="30"
              height="40"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="3"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <path d="M12 5v14M19 12l-7 7-7-7" />
            </svg>
            <div class="wave-label">WAVE</div>
          </div>
        </div>
        <div class="ship-overlay">
          <svg width="50" height="80" viewBox="0 0 60 120">
            <path
              d="M30 5 C10 30, 5 80, 5 100 L55 100 C55 80, 50 30, 30 5 Z"
              fill="#f8fafc"
              stroke="#2563eb"
              stroke-width="3"
            />
            <circle cx="30" cy="15" r="2" fill="#2563eb" />
          </svg>
        </div>
        <div class="compass-tick"></div>
      </div>

      <div class="nav-stats-grid">
        <div class="nav-stat-box">
          <span class="nav-stat-label">Heading</span>
          <div class="nav-stat-val nav-stat-val--heading">{{ compassHeadingText }}</div>
        </div>
        <div class="nav-stat-box">
          <span class="nav-stat-label">Wave Direction</span>
          <div class="nav-stat-val nav-stat-val--wave">{{ waveDirectionText }}</div>
        </div>
        <div class="nav-stat-box">
          <span class="nav-stat-label">Wave Height</span>
          <div class="nav-stat-val nav-stat-val--height">{{ waveHeightText }}</div>
        </div>
        <div class="nav-stat-box">
          <span class="nav-stat-label">Wave Period</span>
          <div class="nav-stat-val nav-stat-val--period">{{ wavePeriodText }}</div>
        </div>
        <div class="nav-stat-box">
          <span class="nav-stat-label">Sea State</span>
          <div class="nav-stat-val nav-stat-val--period">{{ seaStateText }}</div>
        </div>
      </div>

      <div class="impact-note" v-html="impactNote"></div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'NavigationSeaConditions',
  props: {
    currAssetLastData: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      compassMarks: Array.from({ length: 36 }, (_, idx) => idx * 10),
      compassCardinals: [
        { deg: 0, label: 'N' },
        { deg: 90, label: 'E' },
        { deg: 180, label: 'S' },
        { deg: 270, label: 'W' },
      ],
    };
  },
  computed: {
    compassHeading() {
      const heading = this.currAssetLastData?.gps?.track;
      const value = Number(heading);
      if (!Number.isFinite(value)) return null;
      return ((value % 360) + 360) % 360;
    },
    compassHeadingText() {
      if (this.compassHeading === null) return '-';
      return `${this.compassHeading.toFixed(0).padStart(3, '0')}°`;
    },
    waveDirection() {
      const value = this.currAssetLastData?.gps?.waveDir;
      if (!Number.isFinite(Number(value))) return null;
      return ((Number(value) % 360) + 360) % 360;
    },
    waveHeight() {
      const value = this.currAssetLastData?.gps?.waveHeight;
      return Number.isFinite(Number(value)) ? Number(value) : null;
    },
    wavePeriod() {
      const value = this.currAssetLastData?.gps?.wavePeriod;
      return Number.isFinite(Number(value)) ? Number(value) : null;
    },
    waveDirectionText() {
      if (this.waveDirection === null) return '-';
      return `${this.waveDirection.toFixed(0).padStart(3, '0')}°`;
    },
    waveHeightText() {
      if (this.waveHeight === null) return '-';
      return `${this.waveHeight.toFixed(1)}m`;
    },
    wavePeriodText() {
      if (this.wavePeriod === null) return '-';
      return `${this.wavePeriod.toFixed(0)}s`;
    },
    seaStateText() {
      const value = this.currAssetLastData?.gps?.seaState;
      if (!value) return '-';
      return String(value).toUpperCase();
    },
    compassRotationStyle() {
      const heading = this.compassHeading ?? 0;
      return { transform: `rotate(${-heading}deg)` };
    },
    waveRotationStyle() {
      if (this.waveDirection === null) return {};
      const heading = this.compassHeading ?? 0;
      return { transform: `rotate(${this.waveDirection - heading}deg)` };
    },
    impactNote() {
      if (this.waveHeight === null || this.waveDirection === null || this.compassHeading === null) {
        return 'Wave data unavailable.';
      }
      const rel = (this.waveDirection - this.compassHeading + 360) % 360;
      if (this.waveHeight > 4) {
        return '<strong>DANGER:</strong> Extreme wave height detected.';
      }
      if (rel > 330 || rel <= 30) {
        return '<strong>Head Seas:</strong> Waves from ahead. Slamming risk.';
      }
      if (rel > 60 && rel <= 120) {
        return '<strong>Beam Seas:</strong> Waves from the side. Higher rolling risk.';
      }
      if (rel > 150 && rel <= 210) {
        return '<strong>Following Seas:</strong> Waves from behind.';
      }
      return 'Navigation conditions look safe.';
    },
  },
};
</script>

<style scoped>
.compass-card .iq-card-body {
  padding: 15px 20px;
}

.compass-viewport {
  position: relative;
  width: 100%;
  height: 300px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: radial-gradient(circle, #f8fafc 0%, #f1f5f9 100%);
  border-radius: 16px;
  overflow: hidden;
  margin-bottom: 20px;
  border: 1px solid #e2e8f0;
}

.compass-scale {
  position: absolute;
  width: 260px;
  height: 260px;
  border-radius: 50%;
  transition: transform 0.5s cubic-bezier(0.4, 0, 0.2, 1);
}

.degree-mark {
  position: absolute;
  width: 100%;
  height: 100%;
  text-align: center;
  font-size: 0.5625rem;
  font-weight: bold;
  color: #94a3b8;
}

.degree-mark::before {
  content: "";
  position: absolute;
  top: 5px;
  left: 50%;
  width: 1.5px;
  height: 8px;
  background-color: #cbd5e1;
  transform: translateX(-50%);
}

.degree-label {
  margin-top: 18px;
}

.cardinal-point {
  position: absolute;
  width: 100%;
  height: 100%;
  text-align: center;
  font-size: 1rem;
  font-weight: 900;
  color: #2563eb;
}

.cardinal-label {
  margin-top: 30px;
}

.ship-overlay {
  position: absolute;
  z-index: 10;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.ship-speed-label {
  margin-top: 8px;
  background: rgba(255, 255, 255, 0.9);
  padding: 2px 8px;
  border-radius: 4px;
  border: 1px solid #2563eb;
  font-family: monospace;
  font-size: 0.75rem;
  font-weight: bold;
  color: #2563eb;
}

.wave-indicator {
  position: absolute;
  width: 260px;
  height: 260px;
  pointer-events: none;
  transition: transform 0.5s cubic-bezier(0.4, 0, 0.2, 1);
}

.wave-indicator--hidden {
  opacity: 0;
}

.wave-arrow {
  position: absolute;
  top: 10px;
  left: 50%;
  transform: translateX(-50%);
  color: #e11d48;
  animation: wave-pulse 2s infinite ease-in-out;
}

.wave-label {
  font-size: 0.5rem;
  font-weight: bold;
  text-align: center;
}

.compass-tick {
  position: absolute;
  top: 10px;
  left: 50%;
  transform: translateX(-50%);
  width: 2px;
  height: 12px;
  background: red;
  z-index: 20;
}

.nav-stats-grid {
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  gap: 10px;
  margin-bottom: 16px;
}

.nav-stat-box {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  padding: 10px;
  border-radius: 12px;
  text-align: center;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: center;
  min-height: 64px;
}

.nav-stat-label {
  font-size: 0.5625rem;
  text-transform: uppercase;
  font-weight: 700;
  color: #6b7280;
  display: block;
  margin-bottom: 4px;
}

.nav-stat-val {
  font-size: 0.9375rem;
  font-weight: 700;
  font-family: monospace;
}

.nav-stat-val--heading {
  color: #2563eb;
}

.nav-stat-val--wave {
  color: #e11d48;
}

.nav-stat-val--height {
  color: #16a34a;
}

.nav-stat-val--period {
  color: #7c3aed;
}

.impact-note {
  margin-top: 12px;
  padding: 10px 14px;
  border-radius: 10px;
  font-size: 0.75rem;
  border-left: 4px solid #f59e0b;
  background: #fffbeb;
  color: #92400e;
}

@keyframes wave-pulse {
  0%,
  100% {
    transform: translate(-50%, 0);
    opacity: 0.6;
  }
  50% {
    transform: translate(-50%, 10px);
    opacity: 1;
  }
}
</style>
